"use client";

import type { ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export interface FormFieldErrorProps {
  /**
   * 這則錯誤的 DOM id，必須與輸入框的 aria-describedby 相同。
   * 慣例：`${欄位 name}-error`，例如 name="title" → id="title-error"。
   */
  id: string;
  /** 沒有錯誤時傳 undefined，整個元件不渲染（不留空白節點撐版面）。 */
  message?: ReactNode;
  className?: string;
}

/**
 * 把「輸入框要掛什麼」算好回傳，呼叫端直接展開。
 *
 * 為什麼要有這個 helper：規範 §8 要求欄位級錯誤同時具備 aria-describedby +
 * aria-invalid，而這兩件事寫在**另一個元素**（輸入框）上，跟 <FormFieldError>
 * 隔著一段 JSX。實測全站 16 處手刻紅字沒有任何一處掛過 aria-invalid ——
 * 不是不想做，是「錯誤訊息」與「輸入框狀態」被寫在兩個地方，必然有人只做一半。
 *
 * 把它變成一個回傳值之後，兩者從同一個 id 推導，物理上不可能只做一半。
 *
 * 用法：
 *   const err = errors.title;
 *   <input {...fieldErrorProps("title-error", err)} className={err ? "cui-input cui-input-invalid" : "cui-input"} />
 *   <FormFieldError id="title-error" message={err} />
 */
export function fieldErrorProps(
  id: string,
  message?: unknown,
): { "aria-invalid"?: true; "aria-describedby"?: string } {
  // 沒有錯誤時**不能**回 aria-invalid="false" 再配一個指向空節點的 describedby
  // ——螢幕閱讀器會朗讀一個不存在的說明。乾脆整個屬性不出現。
  return message ? { "aria-invalid": true, "aria-describedby": id } : {};
}

/**
 * 欄位級錯誤訊息（規範 §3 C 類：停留到修正為止）。
 *
 * 顏色一律 --cui-danger（文字用紅），**不是** --cui-danger-solid。
 * 兩者在淺色下同值 #b91c1c，很容易誤以為可以互換；但深色模式下
 * --cui-danger 是 #f87171（紅字疊深底，6.24:1 ✓）而 --cui-danger-solid 是
 * #dc2626（實心底配白字專用）。拿 solid 當文字色，深色模式下對卡片底只有
 * 2.67:1，紅字幾乎融進背景。
 *
 * role="alert"：送出後才出現的驗證錯誤是使用者下一步的依據，必須插播（規範 §8）。
 */
export function FormFieldError({ id, message, className }: FormFieldErrorProps) {
  useInjectStyles();
  if (!message) return null;
  const cls = ["cui-reset", "cui-field-error", className ?? ""].filter(Boolean).join(" ");
  return (
    <span className={cls} id={id} role="alert">
      {/* 形狀線索：色弱使用者不必單靠「紅色」認出這是錯誤（比照 Badge / Toast 的做法） */}
      <span className="cui-field-error-icon" aria-hidden>
        !
      </span>
      <span className="cui-field-error-msg">{message}</span>
    </span>
  );
}
